<template>
  <div class="ai-settings-container">
    <AiSettingsSkeleton v-if="loading" />

    <div
      v-else
      class="form-wrapper"
    >
      <section class="settings-section">
        <h2 class="text-h2 section-title mb-4">
          AI Writing Assistance
        </h2>
        <p class="section-description mb-5">
          Enable First Draft to generate initial job posts and profiles, plus AI writing suggestions
          to improve tone and clarity.
          <a
            href="#"
            class="section-description-link"
            data-beacon-article-sidebar="69cd28049207e139315c3eb2"
            @click.stop
          >Learn more here</a>.
        </p>

        <DpplSlimSwitch
          v-model="aiEnabled"
          :label="aiEnabled ? 'AI Enabled' : 'AI Disabled'"
          active-color="brand"
          :disabled="saving"
          a11y-label-text="Toggle AI writing assistance"
          @update:model-value="save"
        />
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import DpplSlimSwitch from '@/components/datapeople/DpplSlimSwitch.vue';
import AiSettingsSkeleton from '@/components/settings/components/AiSettingsSkeleton.vue';
import { getAiSettings, updateAiSettings } from '@/api/settings';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppSnackbar } from '@/stores/app/snackbar';

const { setAiEnabled } = useAppAuthData();
const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

const loading = ref(true);
const aiEnabled = ref(false);
const saving = ref(false);
const initialAiEnabled = ref(false);

const save = async () => {
  if (saving.value) return;
  if (aiEnabled.value === initialAiEnabled.value) return;

  saving.value = true;

  try {
    await updateAiSettings({ aiEnabled: aiEnabled.value });

    setAiEnabled(aiEnabled.value);

    initialAiEnabled.value = aiEnabled.value;

    addSuccessSnackbar({ text: 'AI settings have been updated' });
  } catch (err) {
    addErrorSnackbar({ text: err?.message || 'Failed to update AI settings' });
  } finally {
    saving.value = false;
  }
};

const loadAiSettings = async () => {
  loading.value = true;

  try {
    const data = await getAiSettings();

    const enabled = data?.aiEnabled || false;

    aiEnabled.value = enabled;
    initialAiEnabled.value = enabled;
  } catch (err) {
    addErrorSnackbar({ text: err?.message || 'Failed to load AI settings' });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadAiSettings();
});
</script>

<style scoped lang="less">
.ai-settings-container {
  width: 100%;
}

.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  border-radius: 10px;
  background: @white;
  padding: 28px 40px 40px;
}

.settings-section {
  width: 100%;
}

.section-title {
  color: @text-darkest;
}

.section-description {
  max-width: 100%;
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 150%;
  color: @text-dark;
}

.section-description-link {
  color: @text-brand;
  font-weight: 500 !important;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
}

</style>
